import {
  ComplianceConfigV1,
  ComplianceConfigV1Schema,
  ComplianceEmailNotificationJobConfigV1,
} from '@csp/config-schemas';
import { EnumUtil } from '@csp/csp-common-enum-util';
import {
  ComplianceNotificationJobConfig,
  EmailJobConfig,
  EmailJobCronPattern,
  Maybe,
  StudyComplianceConfig,
} from '@csp/csp-common-model';
import { CommonHcpWebPath } from '@csp/csp-common-routes';
import { isDefined, JsonValidationService } from '@csp/csp-common-util';
import { RoleType } from '@csp/dmdp-api-common-dto';

const toPatientColumnLink = (patientColumnLink: Maybe<string>): Maybe<CommonHcpWebPath> =>
  EnumUtil.fromMaybeString<CommonHcpWebPath>(patientColumnLink, CommonHcpWebPath);

const toComplianceNotificationJobConfig = (
  emailNotificationJobConfig: ComplianceEmailNotificationJobConfigV1,
): ComplianceNotificationJobConfig => ({
  complianceType: emailNotificationJobConfig.complianceType,
  daily: emailNotificationJobConfig.daily && {
    ...emailNotificationJobConfig.daily,
    patientColumnLink: toPatientColumnLink(emailNotificationJobConfig.daily?.patientColumnLink),
  },
  weekly: emailNotificationJobConfig.weekly && {
    ...emailNotificationJobConfig.weekly,
    patientColumnLink: toPatientColumnLink(emailNotificationJobConfig.weekly?.patientColumnLink),
  },
  monthly: emailNotificationJobConfig.monthly && {
    ...emailNotificationJobConfig.monthly,
    patientColumnLink: toPatientColumnLink(emailNotificationJobConfig.monthly?.patientColumnLink),
  },
});

const toComplianceConfig = (complianceConfigV1: ComplianceConfigV1): StudyComplianceConfig => {
  JsonValidationService.validateJsonNonStrict('ComplianceConfigV1.json', ComplianceConfigV1Schema, complianceConfigV1);

  return {
    patientAggregatedExternalReportUrl: complianceConfigV1.patientAggregatedCompliance.externalReportUrl,
    emailNotificationJobConfigs: complianceConfigV1.emailNotificationJobConfigs?.map(toComplianceNotificationJobConfig),
  };
};

const toRoleTypes = (roles: string[]): RoleType[] =>
  roles.map(roleAsString => EnumUtil.fromString<RoleType>(roleAsString, RoleType)).filter(isDefined);

const hasDeprecatedJobs = (
  conf: Maybe<ComplianceEmailNotificationJobConfigV1[]>,
): conf is ComplianceEmailNotificationJobConfigV1[] =>
  conf?.some(config => config.daily || config.weekly || config.monthly) ?? false;

const toEmailJobConfigs = (complianceConfigV1: ComplianceConfigV1): EmailJobConfig[] => {
  if (hasDeprecatedJobs(complianceConfigV1.emailNotificationJobConfigs)) {
    return complianceConfigV1.emailNotificationJobConfigs.flatMap((config, index) => {
      const { daily, weekly, monthly } = config;

      return [
        daily?.enabled
          ? EmailJobConfig.from({
              code: `DAILY-${index}`,
              cronPattern: EmailJobCronPattern.DAILY,
              roles: toRoleTypes(daily.roles),
              patientColumnLink: daily.patientColumnLink,
              groupBy: undefined,
            })
          : undefined,
        weekly?.enabled
          ? EmailJobConfig.from({
              code: `WEEKLY-${index}`,
              cronPattern: EmailJobCronPattern.WEEKLY,
              roles: toRoleTypes(weekly.roles),
              patientColumnLink: weekly.patientColumnLink,
              groupBy: undefined,
            })
          : undefined,
        monthly?.enabled
          ? EmailJobConfig.from({
              code: `MONTHLY-${index}`,
              cronPattern: EmailJobCronPattern.MONTHLY,
              roles: toRoleTypes(monthly.roles),
              patientColumnLink: monthly.patientColumnLink,
              groupBy: undefined,
            })
          : undefined,
      ].filter(isDefined);
    });
  }

  return (
    complianceConfigV1.notifications?.compliance?.emails?.map(
      ({ code, cronPattern, roles, patientColumnLink, groupBy }) =>
        EmailJobConfig.from({ code, cronPattern, roles: toRoleTypes(roles), patientColumnLink, groupBy }),
    ) ?? []
  );
};

export const ComplianceConfigService = {
  toComplianceConfig,
  toEmailJobConfigs,
};
